import type { TelemetryConfigInput } from "../schema/config.js";
import { parseTelemetryConfig } from "../schema/config.js";
import { createProviderFromConfig } from "../providers/factory.js";
import type { ProviderCreateContext } from "../providers/plugin-context.js";
import { SentryClientRegistry } from "../providers/sentry/registry.js";
import { providerMatchesRuntime } from "../providers/runtime-filter.js";
import { BreadcrumbBuffer } from "./breadcrumbs.js";
import {
  buildCustomEvent,
  buildErrorEvent,
  buildLogEvent,
  buildMessageEvent,
  buildResourceErrorEvent,
} from "./events.js";
import { toErrorPayload } from "./normalize.js";
import { getRequestContext } from "./request-context.js";
import { resolveRuntime, type TelemetryRuntimeInput } from "./runtime.js";
import type {
  CaptureOptions,
  RequestContext,
  TelemetryContext,
  TelemetryEvent,
  TelemetryFramework,
  TelemetryLevel,
  TelemetryMessageLevel,
  TelemetryProvider,
  TelemetryProviderType,
  TelemetryRuntime,
} from "./types.js";
import { isLogEventKind } from "./types.js";

export interface TelemetryClientOptions extends TelemetryConfigInput {
  /** Additional programmatic providers (not part of the config schema) */
  runtimeProviders?: TelemetryProvider[];
  /** Injected Sentry SDK clients for this telemetry instance */
  sentryRegistry?: SentryClientRegistry;
}

export class TelemetryClient {
  readonly config: ReturnType<typeof parseTelemetryConfig>;
  readonly runtime: TelemetryRuntime;
  private readonly providers: TelemetryProvider[];
  private readonly baseContext: Partial<TelemetryContext>;
  private readonly breadcrumbs: BreadcrumbBuffer;
  private readonly breadcrumbsEnabled: boolean;
  private defaultFramework: TelemetryFramework = "browser";

  constructor(options: TelemetryClientOptions) {
    const {
      runtimeProviders,
      runtime: runtimeInput,
      sentryRegistry,
      ...configInput
    } = options;
    this.config = parseTelemetryConfig(configInput);
    this.runtime = resolveRuntime(
      (runtimeInput ?? this.config.runtime) as TelemetryRuntimeInput,
    );
    const providerCtx: ProviderCreateContext = {
      runtime: this.runtime,
      sentryRegistry: sentryRegistry ?? new SentryClientRegistry(),
    };

    const activeConfigs = this.config.providers.filter((p) =>
      providerMatchesRuntime(p, this.runtime),
    );

    const fromSchema = activeConfigs.map((c) =>
      createProviderFromConfig(c, this.runtime, providerCtx),
    );
    this.providers = [...fromSchema, ...(runtimeProviders ?? [])];

    if (this.providers.length === 0) {
      throw new Error(
        "[telemetry] At least one provider is required for this runtime. Pass providers in createTelemetry() or use Console().",
      );
    }

    this.baseContext = {
      appId: this.config.appId,
      environment: this.config.environment,
      release: this.config.release,
      runtime: this.runtime,
      ...this.config.context,
    };

    const breadcrumbConfig = this.config.breadcrumbs;
    this.breadcrumbsEnabled = breadcrumbConfig?.enabled !== false;
    this.breadcrumbs = new BreadcrumbBuffer(breadcrumbConfig?.maxSize ?? 50);
  }

  get isClient(): boolean {
    return this.runtime === "client";
  }

  get isServer(): boolean {
    return this.runtime === "server";
  }

  setFramework(framework: TelemetryFramework): void {
    this.defaultFramework = framework;
  }

  setContext(partial: Partial<TelemetryContext>): void {
    Object.assign(this.baseContext, partial);
  }

  /** Attach per-request SSR context (URL, route, request id) on the server */
  setRequestContext(partial: RequestContext): void {
    if (partial.url) this.baseContext.url = partial.url;
    if (partial.route) this.baseContext.route = partial.route;
    if (partial.userAgent) this.baseContext.userAgent = partial.userAgent;
    if (partial.requestId) {
      this.baseContext.tags = {
        ...this.baseContext.tags,
        request_id: partial.requestId,
      };
    }
  }

  setUser(userId: string, tags?: Record<string, string>): void {
    this.baseContext.userId = userId;
    if (tags) {
      this.baseContext.tags = { ...this.baseContext.tags, ...tags };
    }
  }

  /** Record a breadcrumb without sending to providers */
  addBreadcrumb(
    message: string,
    options?: {
      category?: string;
      level?: TelemetryLevel;
      data?: Record<string, unknown>;
    },
  ): void {
    if (!this.breadcrumbsEnabled) return;
    this.breadcrumbs.push({
      timestamp: new Date().toISOString(),
      message,
      category: options?.category,
      level: options?.level ?? "info",
      data: options?.data,
    });
  }

  captureError(error: unknown, options: CaptureOptions = {}): void {
    this.dispatch("error", error, options);
  }

  captureErrorForProvider(
    providerType: TelemetryProviderType,
    error: unknown,
    options: CaptureOptions = {},
  ): void {
    this.dispatch("error", error, options, providerType);
  }

  captureUnhandledRejection(reason: unknown, options: CaptureOptions = {}): void {
    this.dispatch("unhandled_rejection", reason, {
      ...options,
      handled: false,
    });
  }

  captureUnhandledRejectionForProvider(
    providerType: TelemetryProviderType,
    reason: unknown,
    options: CaptureOptions = {},
  ): void {
    this.dispatch("unhandled_rejection", reason, { ...options, handled: false }, providerType);
  }

  captureRouteError(
    error: unknown,
    routeContext: { routeId?: string; route?: string },
    options: CaptureOptions = {},
  ): void {
    this.dispatch("route_error", error, {
      ...options,
      framework: options.framework ?? "react-router",
      routeId: routeContext.routeId,
      metadata: { ...options.metadata, route: routeContext.route },
    });
    if (routeContext.route) {
      this.baseContext.route = routeContext.route;
    }
  }

  captureRouteErrorForProvider(
    providerType: TelemetryProviderType,
    error: unknown,
    routeContext: { routeId?: string; route?: string },
    options: CaptureOptions = {},
  ): void {
    this.dispatch(
      "route_error",
      error,
      {
        ...options,
        framework: options.framework ?? "react-router",
        routeId: routeContext.routeId,
        metadata: { ...options.metadata, route: routeContext.route },
      },
      providerType,
    );
    if (routeContext.route) {
      this.baseContext.route = routeContext.route;
    }
  }

  captureResourceError(event: ErrorEvent): void {
    if (this.runtime !== "client") return;

    const target = event.target as HTMLElement | null;
    const src = target
      ? (target as HTMLImageElement & { src?: string }).src ??
        (target as HTMLScriptElement).src ??
        (target as HTMLLinkElement).href
      : undefined;

    const eventRecord = buildResourceErrorEvent(
      event.message || `Failed to load resource: ${src ?? "unknown"}`,
      this.eventBase(),
      { tagName: target?.tagName, src },
    );

    void this.emit(eventRecord, { sample: true });
  }

  /** Structured log — sent to all providers; also stored in the breadcrumb buffer */
  log(
    level: TelemetryLevel,
    message: string,
    attributes?: Record<string, unknown>,
  ): void {
    const eventRecord = buildLogEvent(level, message, this.eventBase(), attributes);
    void this.emit(eventRecord, { buffer: true });
  }

  logForProvider(
    providerType: TelemetryProviderType,
    level: TelemetryLevel,
    message: string,
    attributes?: Record<string, unknown>,
  ): void {
    const eventRecord = buildLogEvent(level, message, this.eventBase(), attributes);
    void this.emit(eventRecord, { buffer: true }, providerType);
  }

  /** One-line message — sent to all providers */
  captureMessage(
    message: string,
    level: TelemetryMessageLevel = "info",
    extra?: Record<string, unknown>,
  ): void {
    const eventRecord = buildMessageEvent(
      message,
      level,
      this.eventBase(),
      extra,
    );
    void this.emit(eventRecord, { buffer: true });
  }

  captureMessageForProvider(
    providerType: TelemetryProviderType,
    message: string,
    level: TelemetryMessageLevel = "info",
    extra?: Record<string, unknown>,
  ): void {
    const eventRecord = buildMessageEvent(message, level, this.eventBase(), extra);
    void this.emit(eventRecord, { buffer: true }, providerType);
  }

  /** Named custom event (maps to kind `custom`) */
  captureCustom(
    name: string,
    message: string,
    metadata?: Record<string, unknown>,
  ): void {
    const eventRecord = buildCustomEvent(name, message, this.eventBase(), metadata);
    void this.emit(eventRecord, { buffer: true });
  }

  captureCustomForProvider(
    providerType: TelemetryProviderType,
    name: string,
    message: string,
    metadata?: Record<string, unknown>,
  ): void {
    const eventRecord = buildCustomEvent(name, message, this.eventBase(), metadata);
    void this.emit(eventRecord, { buffer: true }, providerType);
  }

  async flush(): Promise<void> {
    await this.flushProviders(this.providers);
  }

  async flushForProvider(providerType: TelemetryProviderType): Promise<void> {
    await this.flushProviders(this.providersForType(providerType));
  }

  private eventBase() {
    return {
      runtime: this.runtime,
      framework: this.defaultFramework,
      context: this.buildContext(),
    };
  }

  private dispatch(
    kind: "error" | "unhandled_rejection" | "route_error",
    error: unknown,
    options: CaptureOptions,
    providerType?: TelemetryProviderType,
  ): void {
    const eventRecord = buildErrorEvent(
      kind,
      toErrorPayload(error),
      this.eventBase(),
      options,
    );

    if (this.breadcrumbsEnabled) {
      eventRecord.breadcrumbs = this.breadcrumbs.snapshot();
    }

    void this.emit(eventRecord, { sample: true }, providerType);
  }

  private providersForType(providerType: TelemetryProviderType): TelemetryProvider[] {
    return this.providers.filter((p) => p.type === providerType);
  }

  private async emit(
    event: TelemetryEvent,
    options: { sample?: boolean; buffer?: boolean } = {},
    providerType?: TelemetryProviderType,
  ): Promise<void> {
    if (options.sample && !this.shouldSample()) return;

    if (options.buffer && this.breadcrumbsEnabled && isLogEventKind(event.kind)) {
      this.breadcrumbs.pushFromEvent(event);
    }

    const targets = providerType
      ? this.providersForType(providerType)
      : this.providers;

    if (providerType && targets.length === 0) {
      console.warn(
        `[telemetry] No "${providerType}" provider on ${this.runtime} runtime. Add it in telemetry.config.`,
      );
      return;
    }

    await this.sendToProviders(event, targets);
  }

  private buildContext(): TelemetryContext {
    const requestCtx = getRequestContext();

    const ctx: TelemetryContext = {
      appId: this.baseContext.appId ?? this.config.appId,
      environment: this.baseContext.environment,
      release: this.baseContext.release,
      runtime: this.runtime,
      userId: this.baseContext.userId,
      sessionId: this.baseContext.sessionId,
      route: this.baseContext.route ?? requestCtx?.route,
      tags: this.baseContext.tags,
      extra: this.baseContext.extra,
    };

    if (this.runtime === "client" && typeof window !== "undefined") {
      ctx.url = this.baseContext.url ?? window.location.href;
      ctx.userAgent = this.baseContext.userAgent ?? navigator.userAgent;
    } else if (requestCtx) {
      ctx.url = this.baseContext.url ?? requestCtx.url;
      ctx.userAgent = this.baseContext.userAgent ?? requestCtx.userAgent;
      ctx.route = ctx.route ?? requestCtx.route;
    } else {
      ctx.url = this.baseContext.url;
      ctx.userAgent = this.baseContext.userAgent;
    }

    return ctx;
  }

  private shouldSample(): boolean {
    const rate = this.config.sampleRate ?? 1;
    if (rate >= 1) return true;
    if (rate <= 0) return false;
    return Math.random() < rate;
  }

  /** Runs `prepare()` on each provider that defines it (e.g. eager Sentry.init). */
  async prepareProviders(): Promise<void> {
    await Promise.allSettled(
      this.providers.map((provider) => provider.prepare?.()),
    );
  }

  private async flushProviders(providers: TelemetryProvider[]): Promise<void> {
    await Promise.all(
      providers.map((p) => p.flush?.()).filter(Boolean) as Promise<void>[],
    );
  }

  private async sendToProviders(
    event: TelemetryEvent,
    providers: TelemetryProvider[] = this.providers,
  ): Promise<void> {
    await Promise.allSettled(providers.map((provider) => provider.send(event)));
  }
}

export function initClientTelemetry(
  options: Omit<TelemetryClientOptions, "runtime">,
): TelemetryClient {
  return new TelemetryClient({ ...options, runtime: "client" });
}

export function initServerTelemetry(
  options: Omit<TelemetryClientOptions, "runtime">,
): TelemetryClient {
  return new TelemetryClient({ ...options, runtime: "server" });
}
